import React, { useCallback, useEffect, useState } from 'react';
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
} from 'recharts';
import { fetchWasteAnalysis, InventoryApiError } from '../../services/inventoryApi';
import './WasteAnalytics.css';

interface WasteAnalyticsProps {
  selectedStore: string;
  enterpriseId: string;
}

interface WasteBreakdown {
  ingredientId: string;
  ingredientName: string;
  wastePercent: number;
  wasteCost: number;
  spoilageCost?: number;
  prepWasteCost?: number;
  overproductionCost?: number;
  shelfLifeDays?: number;
  consumptionDays?: number;
}

interface WasteAnalysis {
  storeId: string;
  totalWasteCost?: number;
  breakdown?: WasteBreakdown[];
  categorySplit?: {
    spoilage?: number;
    prepWaste?: number;
    overproduction?: number;
  };
}

const PIE_COLORS = ['#ef4444', '#f59e0b', '#6366f1'];

const WasteAnalytics: React.FC<WasteAnalyticsProps> = ({ selectedStore }) => {
  const [data, setData] = useState<WasteAnalysis | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!selectedStore) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetchWasteAnalysis(selectedStore);
      setData(res);
    } catch (e) {
      setError(e instanceof InventoryApiError ? e.message : 'Failed to load waste analysis');
    } finally {
      setLoading(false);
    }
  }, [selectedStore]);

  useEffect(() => {
    load();
  }, [load]);

  const breakdown = data?.breakdown || [];
  const categorySplit = data?.categorySplit || {};

  const pieData = [
    { name: 'Spoilage', value: categorySplit.spoilage || 0 },
    { name: 'Prep Waste', value: categorySplit.prepWaste || 0 },
    { name: 'Overproduction', value: categorySplit.overproduction || 0 },
  ].filter((d) => d.value > 0);

  const topWaste = [...breakdown]
    .sort((a, b) => (b.wasteCost || 0) - (a.wasteCost || 0))
    .slice(0, 10)
    .map((b) => ({ name: b.ingredientName, cost: b.wasteCost }));

  return (
    <div className="waste">
      <div className="waste__header">
        <div>
          <h2>Waste Analytics</h2>
          <p className="waste__sub">
            Per-ingredient waste, root cause split, top cost contributors
          </p>
        </div>
        {data?.totalWasteCost != null && (
          <div className="waste__total">
            <span>Total Waste Cost</span>
            <strong>${data.totalWasteCost.toFixed(2)}</strong>
          </div>
        )}
      </div>

      {loading && <div className="waste__state">Loading waste analytics...</div>}
      {error && <div className="waste__error">{error}</div>}

      {!loading && !error && data && (
        <>
          <div className="waste__charts">
            <div className="waste__chart-card">
              <h3>Root Cause Split</h3>
              {pieData.length === 0 ? (
                <div className="waste__state">No category data yet.</div>
              ) : (
                <ResponsiveContainer width="100%" height={260}>
                  <PieChart>
                    <Pie
                      data={pieData}
                      dataKey="value"
                      nameKey="name"
                      innerRadius={50}
                      outerRadius={90}
                    >
                      {pieData.map((entry, index) => (
                        <Cell key={entry.name} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>

            <div className="waste__chart-card">
              <h3>Top 10 Waste Cost Contributors</h3>
              {topWaste.length === 0 ? (
                <div className="waste__state">No ingredient data yet.</div>
              ) : (
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={topWaste} layout="vertical" margin={{ left: 80 }}>
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                    <XAxis type="number" />
                    <YAxis dataKey="name" type="category" width={140} />
                    <Tooltip formatter={(v: number) => `$${Number(v).toFixed(2)}`} />
                    <Bar dataKey="cost" fill="#ef4444" />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          <div className="waste__table-card">
            <h3>Shelf-Life vs Consumption</h3>
            {breakdown.length === 0 ? (
              <div className="waste__state">No ingredients tracked yet.</div>
            ) : (
              <div className="waste__table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Ingredient</th>
                      <th>Waste %</th>
                      <th>Waste $</th>
                      <th>Shelf Life (d)</th>
                      <th>Consumption (d)</th>
                      <th>Ratio</th>
                    </tr>
                  </thead>
                  <tbody>
                    {breakdown.map((b) => {
                      const ratio =
                        b.shelfLifeDays && b.consumptionDays
                          ? (b.shelfLifeDays / b.consumptionDays).toFixed(2)
                          : '-';
                      return (
                        <tr key={b.ingredientId}>
                          <td>{b.ingredientName}</td>
                          <td>{(b.wastePercent * 100).toFixed(1)}%</td>
                          <td>${b.wasteCost.toFixed(2)}</td>
                          <td>{b.shelfLifeDays ?? '-'}</td>
                          <td>{b.consumptionDays ?? '-'}</td>
                          <td>{ratio}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default WasteAnalytics;
